/** CP Declaration panel — generate · edit · sign · lock · insert into CPR. */
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus, Download, LockKey, PaperPlaneTilt, Sparkle, Warning, PencilSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  listCPProfiles, listCPDeclarations, listCPDocuments,
  generateCPDeclaration, editCPDeclaration, signCPDeclaration,
  insertCPDeclarationIntoCPR, downloadCPDeclarationPdf,
} from "@/lib/api";
import { CPField, CPTextInput, StatusBadge, ChecklistRow } from "@/pages/wizard/cp/_cpShared";


const STANDARDS = ["JORC", "NI 43-101", "SAMREC", "PERC",
                    "CRIRSCO-Aligned", "UNFC", "Custom Company Standard"];


export default function CPDeclarationPanel({ projectId, summary, onRefresh }) {
  const qc = useQueryClient();
  const declQ = useQuery({
    queryKey: ["cp-declarations", projectId],
    queryFn:  () => listCPDeclarations(projectId),
    enabled:  !!projectId,
  });
  const profilesQ = useQuery({
    queryKey: ["cp-profiles", projectId],
    queryFn:  () => listCPProfiles(projectId),
    enabled:  !!projectId,
  });
  const docsQ = useQuery({
    queryKey: ["cp-docs", projectId],
    queryFn:  () => listCPDocuments(projectId),
    enabled:  !!projectId,
  });

  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({
    cp_profile_id: "",
    reporting_standard: summary?.reporting_standard || "JORC",
    effective_date: "",
    report_title: "",
  });

  const [signOpen, setSignOpen] = useState(false);
  const [signState, setSignState] = useState({
    decl_id: null,
    signature_document_id: "",
    seal_document_id: "",
    authorization_confirmed: false,
  });

  const [editingId, setEditingId] = useState(null);
  const [draftText, setDraftText] = useState("");

  const generate = useMutation({
    mutationFn: () => generateCPDeclaration(projectId, gen),
    onSuccess:  (d) => {
      toast.success(`Declaration v${d.version} generated`);
      qc.invalidateQueries({ queryKey: ["cp-declarations", projectId] });
      onRefresh?.();
      setGenOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const edit = useMutation({
    mutationFn: () => editCPDeclaration(projectId, editingId, { text: draftText }),
    onSuccess:  (d) => {
      const forked = d.status === "draft" && d.forked_from;
      toast.success(forked
        ? `Locked · forked new draft v${d.version}`
        : "Declaration updated");
      qc.invalidateQueries({ queryKey: ["cp-declarations", projectId] });
      setEditingId(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const sign = useMutation({
    mutationFn: () => signCPDeclaration(projectId, signState.decl_id, {
      signature_document_id: signState.signature_document_id,
      seal_document_id:      signState.seal_document_id || null,
      authorization_confirmed: signState.authorization_confirmed,
      signing_method:        "stored_uploaded_signature",
    }),
    onSuccess:  () => {
      toast.success("Declaration signed and locked");
      qc.invalidateQueries({ queryKey: ["cp-declarations", projectId] });
      onRefresh?.();
      setSignOpen(false);
      setSignState({ decl_id: null, signature_document_id: "",
                       seal_document_id: "", authorization_confirmed: false });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message),
  });

  const insertCpr = useMutation({
    mutationFn: (declId) => insertCPDeclarationIntoCPR(projectId, declId),
    onSuccess:  () => toast.success("Declaration inserted into CPR"),
    onError:    (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const declarations = declQ.data?.declarations || [];
  const profiles     = profilesQ.data?.profiles || [];
  const profileById  = (id) => profiles.find((p) => p.id === id);
  const docsByProfile = useMemo(() => {
    const map = {};
    (docsQ.data?.documents || []).forEach((d) => {
      (map[d.cp_profile_id] ??= []).push(d);
    });
    return map;
  }, [docsQ.data]);

  const signatureDocs = signState.decl_id
    ? (docsByProfile[declarations.find((d) => d.id === signState.decl_id)?.cp_profile_id] || [])
        .filter((d) => d.doc_type === "Signature" && d.verified)
    : [];
  const sealDocs = signState.decl_id
    ? (docsByProfile[declarations.find((d) => d.id === signState.decl_id)?.cp_profile_id] || [])
        .filter((d) => d.doc_type === "Seal" && d.verified)
    : [];

  const openEdit = (d) => { setEditingId(d.id); setDraftText(d.text || ""); };
  const openSign = (d) => {
    setSignState({ decl_id: d.id, signature_document_id: "",
                     seal_document_id: "", authorization_confirmed: false });
    setSignOpen(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {declarations.length} declaration(s) · reporting standard {summary?.reporting_standard || "JORC"}
        </div>
        <Button size="sm" onClick={() => setGenOpen(true)}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    disabled={profiles.length === 0}
                    data-testid="w-cp-decl-generate">
          <Sparkle size={11} className="mr-1" /> Generate declaration
        </Button>
      </div>

      {declarations.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded p-6 text-center text-[12px] text-slate-500">
          No declarations yet. Generate one after completing profile, responsibilities, site visits and verifying the required documents.
        </div>
      )}

      <div className="space-y-3">
        {declarations.map((d) => (
          <div key={d.id} className="border border-slate-200 rounded bg-white p-3"
                data-testid={`w-cp-decl-${d.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <b className="text-[13px]">v{d.version}</b>
              <StatusBadge status={d.status} />
              <span className="text-[11px] font-mono text-slate-500">
                {profileById(d.cp_profile_id)?.full_name || "—"} · {d.reporting_standard}
              </span>
              {d.signed_at && (
                <span className="text-[10px] text-emerald-600 font-mono ml-2">
                  <LockKey size={11} className="inline mr-1 -mt-0.5" />
                  signed {d.signed_at.slice(0, 10)}
                </span>
              )}
              <div className="ml-auto flex gap-1">
                {d.status !== "signed" && d.status !== "superseded" && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]"
                              onClick={() => openEdit(d)}
                              data-testid={`w-cp-decl-edit-${d.id}`}>
                    <PencilSimple size={10} className="mr-1" /> Edit
                  </Button>
                )}
                {d.status === "draft" && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]"
                              onClick={() => openSign(d)}
                              data-testid={`w-cp-decl-sign-${d.id}`}>
                    <LockKey size={10} className="mr-1" /> Sign
                  </Button>
                )}
                {d.status === "signed" && (
                  <Button size="sm" className="h-6 text-[10px] bg-emerald-600 text-white"
                              onClick={() => insertCpr.mutate(d.id)}
                              disabled={insertCpr.isPending}
                              data-testid={`w-cp-decl-insert-${d.id}`}>
                    <PaperPlaneTilt size={10} className="mr-1" /> Insert into CPR
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-6 text-[10px]"
                            onClick={() => downloadCPDeclarationPdf(projectId, d.id, d.version)}
                            data-testid={`w-cp-decl-pdf-${d.id}`}>
                  <Download size={10} className="mr-1" /> PDF
                </Button>
              </div>
            </div>
            <div className="text-[10px] font-mono text-slate-400 border-l-2 border-slate-200 pl-2 whitespace-pre-wrap max-h-40 overflow-y-auto">
              {(d.text || "").slice(0, 800)}
              {d.text && d.text.length > 800 ? "…" : ""}
            </div>
          </div>
        ))}
      </div>

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate CP Declaration</DialogTitle>
            <DialogDescription className="text-[11px]">
              The declaration is composed from the approved CP profile, responsibilities and site visits.
              You can edit the text before signing.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <CPField label="CP">
              <Select value={gen.cp_profile_id}
                        onValueChange={(v) => setGen({ ...gen, cp_profile_id: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-decl-cp">
                  <SelectValue placeholder="Select CP" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Reporting standard">
              <Select value={gen.reporting_standard}
                        onValueChange={(v) => setGen({ ...gen, reporting_standard: v })}>
                <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-decl-standard">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STANDARDS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CPField>
            <CPField label="Effective date">
              <CPTextInput type="date" value={gen.effective_date}
                             onChange={(v) => setGen({ ...gen, effective_date: v })}
                             testId="w-cp-decl-date" />
            </CPField>
            <CPField label="Report title">
              <CPTextInput value={gen.report_title}
                             onChange={(v) => setGen({ ...gen, report_title: v })}
                             placeholder="Chunya Gold Project — Mineral Resource CPR"
                             testId="w-cp-decl-title" />
            </CPField>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white"
                       disabled={!gen.cp_profile_id || generate.isPending}
                       onClick={() => generate.mutate()}
                       data-testid="w-cp-decl-generate-save">
              {generate.isPending ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editingId} onOpenChange={(o) => !o && setEditingId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit CP declaration text</DialogTitle>
            <DialogDescription className="text-[11px]">
              If this declaration is already signed, saving will lock it and create a new unsigned version.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={draftText} onChange={(e) => setDraftText(e.target.value)}
                       rows={20} className="text-[11px] font-mono"
                       data-testid="w-cp-decl-edit-text" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white"
                       disabled={edit.isPending}
                       onClick={() => edit.mutate()}
                       data-testid="w-cp-decl-edit-save">
              {edit.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sign & Lock CP Declaration</DialogTitle>
            <DialogDescription className="text-[11px]">
              A signed declaration is locked. Any later edit forks a new unsigned version.
            </DialogDescription>
          </DialogHeader>
          {signatureDocs.length === 0 ? (
            <div className="text-[12px] text-amber-700 border-l-2 border-amber-400 bg-amber-50 p-2 flex items-start gap-2">
              <Warning size={14} className="mt-0.5" />
              <span>No verified Signature document is on file for this CP. Upload a signature file under the Documents tab and click <b>Verify</b> before signing.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              <CPField label="Signature document">
                <Select value={signState.signature_document_id}
                          onValueChange={(v) => setSignState({ ...signState, signature_document_id: v })}>
                  <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-sign-sig">
                    <SelectValue placeholder="Select verified signature" />
                  </SelectTrigger>
                  <SelectContent>
                    {signatureDocs.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.file_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CPField>
              {sealDocs.length > 0 && (
                <CPField label="Professional seal (optional)">
                  <Select value={signState.seal_document_id}
                            onValueChange={(v) => setSignState({ ...signState, seal_document_id: v })}>
                    <SelectTrigger className="h-8 text-[12px]" data-testid="w-cp-sign-seal">
                      <SelectValue placeholder="Select seal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">— none —</SelectItem>
                      {sealDocs.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.file_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CPField>
              )}
              <label className="flex items-start gap-2 text-[12px] border border-slate-300 rounded p-2 bg-slate-50">
                <input type="checkbox" className="mt-1"
                        checked={signState.authorization_confirmed}
                        onChange={(e) => setSignState({ ...signState, authorization_confirmed: e.target.checked })}
                        data-testid="w-cp-sign-auth" />
                <span className="text-slate-700">
                  <b>I authorise the application of the stored signature.</b> The Competent
                  Person has reviewed and approved this declaration and has authorised use
                  of the stored signature.
                </span>
              </label>
              <div className="text-[11px] font-mono">
                <ChecklistRow ok={!!signState.signature_document_id} label="Verified signature selected" />
                <ChecklistRow ok={signState.authorization_confirmed} label="Signing authorization confirmed" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSignOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 text-white"
                       disabled={!signState.authorization_confirmed
                                    || !signState.signature_document_id
                                    || sign.isPending}
                       onClick={() => sign.mutate()}
                       data-testid="w-cp-sign-apply">
              <LockKey size={11} className="mr-1" />
              {sign.isPending ? "Signing…" : "Sign & lock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
